import { all, takeLatest, call, put, select } from 'redux-saga/effects';

import { objParser } from '@snapask/common-components/dist/utils/objCamelcaseParser';
import { TUTOR } from '@snapask/common-components/dist/constants/userRoles';

import { getSelectedSubjectId } from '@blueberry/redux/dashboard';
import {
  types,
  fetchAccomplishmentsSuccess,
  fetchAccomplishmentsFailure,
  toggleConceptCheckedSuccess,
  toggleConceptCheckedFailure,
  getConcepts,
  getSubtopics,
  getTopics,
  getConceptCheckedCount,
} from '@blueberry/redux/accomplishment';
import { actions as errorActions } from '@blueberry/redux/error';
import { getRole } from '@blueberry/redux/profile';

import {
  fetchAccomplishments,
  patchConceptChecked,
  fetchAccomplishmentsGraph,
  fetchAccomplishmentsForTeacher,
  fetchAccomplishmentsGraphForTeacher,
} from '@blueberry/services/apis';

export function* fetchAccomplishmentsFlow(action) {
  try {
    const { subjectId, studentId } = action.payload || {
      subjectId: yield select(getSelectedSubjectId),
    };
    const userRole = yield select(getRole);

    // teacher and student have different endpoint
    let fetchAPIs =
      userRole === TUTOR
        ? [
            call(fetchAccomplishmentsForTeacher, subjectId, studentId),
            call(fetchAccomplishmentsGraphForTeacher, subjectId, studentId),
          ]
        : [
            call(fetchAccomplishments, subjectId),
            call(fetchAccomplishmentsGraph, subjectId),
          ];

    const [
      {
        concept_checked_count: conceptCheckedCount,
        concept_count: conceptCount,
        mastery_concept_count: masteryRateCorrect,
        tested_concept_count: masteryRateTotal,
        topics: sourceTopics,
      },
      {
        topic_names: topicNames,
        series: { self: selfSeries, class: classSeries, grade: gradeSeries },
      },
    ] = yield all(fetchAPIs);

    const topics = [];
    const subtopics = [];
    const concepts = [];

    sourceTopics.forEach(sourceTopic => {
      sourceTopic.subtopics.forEach(sourceSubtopic => {
        sourceSubtopic.concepts.forEach(sourceConcept => {
          const concept = {
            ...sourceConcept,
            topicId: sourceTopic.id,
            subtopicId: sourceSubtopic.id,
          };
          concepts.push(objParser(concept));
        });

        const subtopic = {
          ...sourceSubtopic,
          topicId: sourceTopic.id,
        };
        delete subtopic.concepts;
        subtopics.push(objParser(subtopic));
      });

      const topic = {
        ...sourceTopic,
      };
      delete topic.subtopics;
      topics.push(objParser(topic));
    });

    const accomplishment = {
      conceptCheckedCount,
      conceptCount,
      masteryRateTotal,
      masteryRate:
        masteryRateTotal === 0
          ? 0
          : Math.round((masteryRateCorrect / masteryRateTotal) * 100),
      topics,
      subtopics,
      concepts,
      topicRateChart: {
        topicNames,
        selfSeries,
        classSeries,
        gradeSeries,
      },
    };
    yield put(fetchAccomplishmentsSuccess(accomplishment));
  } catch (err) {
    yield put(fetchAccomplishmentsFailure(err));
    yield put(errorActions.sendError(err));
  }
}

export function* toggleConceptCheckedFlow(action) {
  try {
    const { conceptId, subtopicId, topicId, isChecked } = action.payload;

    yield call(patchConceptChecked, { subtopicId, conceptId, isChecked });

    const count = isChecked ? +1 : -1;
    const concepts = yield select(getConcepts);
    const selectedConcept = concepts.find(
      p =>
        p.id === conceptId &&
        p.subtopicId === subtopicId &&
        p.topicId === topicId
    );
    selectedConcept.isChecked = isChecked;

    const subtopics = yield select(getSubtopics);
    const selectedSubtopic = subtopics.find(
      p => p.id === subtopicId && p.topicId === topicId
    );
    selectedSubtopic.conceptCheckedCount += count;

    const topics = yield select(getTopics);
    const selectedTopic = topics.find(p => p.id === topicId);
    selectedTopic.conceptCheckedCount += count;

    let conceptCheckedCount = yield select(getConceptCheckedCount);
    conceptCheckedCount += count;

    yield put(
      toggleConceptCheckedSuccess({
        concepts,
        subtopics,
        topics,
        conceptCheckedCount,
      })
    );
  } catch (err) {
    yield put(toggleConceptCheckedFailure(err));
    yield put(errorActions.sendError(err));
  }
}

export default function*() {
  yield all([
    takeLatest(types.FETCH_ACCOMPLISHMENTS, fetchAccomplishmentsFlow),
    takeLatest(types.TOGGLE_CONCEPT_CHECKED, toggleConceptCheckedFlow),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/accomplishment.js